import { oauthConfig, linkedInAuthUrl } from '../config/oauth.config';
import { getRuntimeConfig, getRuntimeConfigOrDefault } from '../utils/runtime-config';
import { API_BASE_URL } from '../config/api.config';
import { sessionService } from './session.service';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  provider: 'google' | 'linkedin' | 'email';
  resume?: {
    url: string;
    uploadedAt: string;
  };
  salaryRange?: {
    min: number;
    max: number;
  };
  skills?: string[];
  companyId?: string;
  companyName?: string;
}

class AuthService {
  private tokenKey = 'jobometry_auth_token';
  private stateKey = 'jobometry_oauth_state';

  async login(provider: 'google' | 'linkedin'): Promise<void> {
    // Check if we should use mock authentication
    const useBackendMockAuth = getRuntimeConfig('REACT_APP_USE_BACKEND_MOCK_AUTH') === 'true';
    const useMockAuth = getRuntimeConfig('REACT_APP_USE_MOCK_AUTH') === 'true';
    const useRealAuth = getRuntimeConfig('REACT_APP_USE_REAL_AUTH') === 'true';
    const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
    
    // Use mock SSO if any mock auth flag is set or in development without real auth
    if (useBackendMockAuth || useMockAuth || (isDevelopment && !useRealAuth)) {
      console.log(`🔐 Mock SSO active - simulating ${provider} OAuth flow`);
      await this.mockSSOFlow(provider);
      return;
    }
    
    // Check if we should force real auth
    const forceRealAuth = getRuntimeConfig('REACT_APP_FORCE_REAL_AUTH') === 'true';
    
    // Development mode - simulate OAuth flow (unless forced to use real auth)
    if (isDevelopment && !forceRealAuth && !this.hasRealCredentials(provider)) {
      console.warn(`${provider} OAuth not configured. Using mock authentication.`);
      console.warn('To use real OAuth, set up credentials in .env file or set REACT_APP_FORCE_REAL_AUTH=true');
      
      // Simulate OAuth flow with mock data
      setTimeout(() => {
        this.handleAuthSuccess({
          token: 'mock-jwt-token-' + Date.now(),
          user: {
            id: 'mock-' + Date.now(),
            email: `demo@${provider}.com`,
            name: `Demo User (${provider})`,
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${provider}`,
            provider: provider
          }
        });
      }, 1000);
      return;
    }
    
    // Production mode - real OAuth flow
    const state = this.generateState();
    sessionStorage.setItem(this.stateKey, state);
    
    switch (provider) {
      case 'google':
        // For Google, we need to use the Google Sign-In SDK
        // The actual redirect will be handled by the SDK
        window.location.href = this.getGoogleAuthUrl();
        break;
        
      case 'linkedin':
        window.location.href = linkedInAuthUrl(state);
        break;

      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  }

  private hasRealCredentials(provider: string): boolean {
    switch (provider) {
      case 'google':
        const googleClientId = getRuntimeConfig('REACT_APP_GOOGLE_CLIENT_ID') || process.env.REACT_APP_GOOGLE_CLIENT_ID;
        return !!googleClientId && 
               googleClientId !== '1234567890-abcdefghijklmnopqrstuvwxyz123456.apps.googleusercontent.com';
      case 'linkedin':
        const linkedinClientId = getRuntimeConfig('REACT_APP_LINKEDIN_CLIENT_ID') || process.env.REACT_APP_LINKEDIN_CLIENT_ID;
        return !!linkedinClientId && 
               linkedinClientId !== 'YOUR_LINKEDIN_CLIENT_ID';
      default:
        return false;
    }
  }

  private getGoogleAuthUrl(): string {
    console.log('OAuth Config:', {
      clientId: oauthConfig.google.clientId,
      redirectUri: oauthConfig.google.redirectUri
    });
    
    const params = new URLSearchParams({
      client_id: oauthConfig.google.clientId,
      redirect_uri: oauthConfig.google.redirectUri,
      response_type: 'code',
      scope: oauthConfig.google.scope,
      access_type: 'offline',
      state: sessionStorage.getItem(this.stateKey) || ''
    });
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    console.log('Generated Auth URL:', authUrl);
    
    return authUrl;
  }

  async loginWithEmail(email: string, password: string): Promise<void> {
    const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
    const useRealAuth = getRuntimeConfig('REACT_APP_USE_REAL_AUTH') === 'true';
    const useMockAuth = getRuntimeConfig('REACT_APP_USE_MOCK_AUTH') === 'true';
    const useBackendMockAuth = getRuntimeConfig('REACT_APP_USE_BACKEND_MOCK_AUTH') === 'true';

    if (useBackendMockAuth || useMockAuth || (isDevelopment && !useRealAuth)) {
      await this.mockEmailFlow(email, 'Login User');
      return;
    }

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Invalid email or password');
    }

    const data = await response.json();
    this.handleAuthSuccess(data);
  }

  async registerWithEmail(email: string, password: string, name: string): Promise<void> {
    const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
    const useRealAuth = getRuntimeConfig('REACT_APP_USE_REAL_AUTH') === 'true';
    const useMockAuth = getRuntimeConfig('REACT_APP_USE_MOCK_AUTH') === 'true';
    const useBackendMockAuth = getRuntimeConfig('REACT_APP_USE_BACKEND_MOCK_AUTH') === 'true';

    if (useBackendMockAuth || useMockAuth || (isDevelopment && !useRealAuth)) {
      await this.mockEmailFlow(email, name);
      return;
    }

    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Registration failed');
    }

    const data = await response.json();
    this.handleAuthSuccess(data);
  }

  private async mockEmailFlow(email: string, name: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 500));

    const timestamp = Date.now();
    const userId = `email-dev-${timestamp}`;
    const mockUser = {
      id: userId,
      email,
      name,
      provider: 'email' as const,
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(email)}`
    };

    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({
      userId, id: userId, sub: userId,
      email, name, provider: 'email',
      iat: Math.floor(timestamp / 1000),
      exp: Math.floor(timestamp / 1000) + 86400
    }));
    const mockToken = `${header}.${payload}.mock-signature`;

    localStorage.setItem(this.tokenKey, mockToken);
    localStorage.setItem('USE_MOCK_AUTH', 'true');

    window.dispatchEvent(new CustomEvent('auth:login', { detail: mockUser }));
  }

  // Handle Google OAuth response
  async handleGoogleLogin(credential: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      if (!response.ok) {
        throw new Error('Google authentication failed');
      }

      const data = await response.json();
      this.handleAuthSuccess(data);
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  // Handle OAuth callback for LinkedIn
  async handleOAuthCallback(provider: string, code: string, state: string): Promise<void> {
    // Verify state for CSRF protection
    const storedState = sessionStorage.getItem(this.stateKey);
    console.log('OAuth state check:', { urlState: state, storedState, match: state === storedState });
    if (state !== storedState) {
      console.error('State mismatch — URL state:', JSON.stringify(state), 'sessionStorage state:', JSON.stringify(storedState));
      throw new Error(`Invalid state parameter (url=${state?.substring(0, 8)}… stored=${storedState?.substring(0, 8) ?? 'null'})`);
    }
    sessionStorage.removeItem(this.stateKey);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/${provider}/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, state }),
      });

      if (!response.ok) {
        throw new Error(`${provider} authentication failed`);
      }

      const data = await response.json();

      // Backend returns { access_token, refresh_token, token_type, expires_in }
      if (data.access_token) {
        // Store token first
        localStorage.setItem(this.tokenKey, data.access_token);

        // Fetch user profile with the new token
        const user = await this.getMe();
        if (!user) {
          throw new Error('Failed to fetch user profile after authentication');
        }

        // Create session
        sessionService.createSession(data.access_token, {
          userId: user.id,
          email: user.email,
          name: user.name
        });

        // Notify app
        window.dispatchEvent(new CustomEvent('auth:login', { detail: user }));

        // Redirect
        const redirectUrl = sessionStorage.getItem('auth_redirect') || '/';
        sessionStorage.removeItem('auth_redirect');
        window.location.href = redirectUrl;
      } else {
        // Legacy format: { token, user }
        this.handleAuthSuccess(data);
      }
    } catch (error) {
      console.error(`${provider} callback error:`, error);
      throw error;
    }
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  private async mockSSOFlow(provider: 'google' | 'linkedin'): Promise<void> {
    try {
      // Check if we should use backend mock auth
      const useBackendMockAuth = getRuntimeConfig('REACT_APP_USE_BACKEND_MOCK_AUTH') === 'true';
      
      // Skip backend mock auth redirect - use frontend mock auth instead
      // The backend mock auth is handled through the normal OAuth endpoints
      // but returns mock data instead of real OAuth data
      
      // Original frontend mock auth flow
      console.log(`🔄 Starting mock ${provider} SSO flow...`);
      
      // Simulate network delay for realistic feel
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate deterministic but unique user data
      const timestamp = Date.now();
      const userId = `${provider}-dev-${timestamp}`;
      
      // Provider-specific mock user data
      const mockUsers = {
        google: {
          id: userId,
          email: 'dev.user@gmail.com',
          name: 'Dev User (Google)',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=google-dev',
          provider: 'google' as const
        },
        linkedin: {
          id: userId,
          email: 'dev.user@linkedin.com',
          name: 'Dev User (LinkedIn)',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=linkedin-dev',
          provider: 'linkedin' as const
        }
      };
      
      const mockUser = mockUsers[provider];
      
      // Generate mock JWT token with proper format
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify({ 
        userId: userId,
        id: userId,
        sub: userId,
        email: mockUser.email,
        name: mockUser.name,
        provider: provider,
        iat: Math.floor(timestamp / 1000),
        exp: Math.floor(timestamp / 1000) + 86400 // 24 hours
      }));
      const signature = 'mock-signature';
      const mockToken = `${header}.${payload}.${signature}`;
      
      // Log successful mock authentication
      console.log(`✅ Mock ${provider} SSO successful!`);
      console.log(`👤 User: ${mockUser.name} (${mockUser.email})`);
      console.log(`🎫 Token: ${mockToken.substring(0, 20)}...`);
      
      // Store token and mock auth flag
      localStorage.setItem(this.tokenKey, mockToken);
      localStorage.setItem('USE_MOCK_AUTH', 'true');
      
      // Create session (using sessionService if available)
      if (typeof window !== 'undefined' && (window as any).sessionService) {
        (window as any).sessionService.createSession(mockToken, {
          userId: userId,
          email: mockUser.email,
          name: mockUser.name
        });
      }
      
      // Then trigger the auth success event
      window.dispatchEvent(new CustomEvent('auth:login', { detail: mockUser }));
      
      // Don't redirect - let the app handle navigation
      console.log('🔄 Authentication complete - updating UI...');
      
    } catch (error) {
      console.error('❌ Mock SSO error:', error);
      throw error;
    }
  }

  private handleAuthSuccess(response: { token: string; user: UserProfile }) {
    localStorage.setItem(this.tokenKey, response.token);
    
    // Create session cookie
    sessionService.createSession(response.token, {
      userId: response.user.id,
      email: response.user.email,
      name: response.user.name
    });
    
    // Trigger a custom event to notify the app of successful login
    window.dispatchEvent(new CustomEvent('auth:login', { detail: response.user }));
    
    // Redirect to home page or previous page
    const redirectUrl = sessionStorage.getItem('auth_redirect') || '/';
    sessionStorage.removeItem('auth_redirect');
    window.location.href = redirectUrl;
  }

  async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        // Call logout endpoint
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem('USE_MOCK_AUTH');
    }
  }

  async getMe(): Promise<UserProfile | null> {
    const token = this.getToken();
    if (!token) return null;

    // In development with mock auth, decode the mock token directly
    // instead of sending it to the backend (which would reject it)
    const isMockToken = token.endsWith('.mock-signature');
    if (isMockToken) {
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          return {
            id: payload.userId || payload.id,
            email: payload.email,
            name: payload.name,
            provider: payload.provider,
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${payload.provider}-dev`
          };
        }
      } catch (e) {
        console.log('Failed to decode mock token');
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid - this is expected when not logged in
          localStorage.removeItem(this.tokenKey);
          return null;
        }
        if (response.status >= 500) {
          console.error('Server error while fetching user:', response.status);
        }
        return null;
      }

      const userData = await response.json();

      // Map backend user data to frontend UserProfile
      return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        avatar: userData.avatar,
        provider: userData.provider,
        resume: userData.resume,
        salaryRange: userData.salary_range,
        skills: userData.skills,
        companyId: userData.company_id,
        companyName: userData.company_name
      };
    } catch (error) {
      console.error('Get user error:', error);
      
      localStorage.removeItem(this.tokenKey);
      return null;
    }
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    const token = this.getToken();
    if (!token) throw new Error('Not authenticated');

    try {
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update profile');
      }
      
      const userData = await response.json();
      
      // Map backend user data to frontend UserProfile
      return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        avatar: userData.avatar,
        provider: userData.provider,
        resume: userData.resume,
        salaryRange: userData.salary_range,
        skills: userData.skills,
        companyId: userData.company_id,
        companyName: userData.company_name
      };
    } catch (error) {
      console.error('Update profile error:', error);
      
      // For demo mode, return updated mock data
      if (process.env.NODE_ENV === 'development' && token.startsWith('mock-')) {
        const currentUser = await this.getMe();
        if (!currentUser) throw new Error('User not found');
        return { ...currentUser, ...updates };
      }
      
      throw error;
    }
  }

  async uploadResume(file: File): Promise<{ url: string; uploadedAt: string }> {
    const token = this.getToken();
    if (!token) throw new Error('Not authenticated');

    const formData = new FormData();
    formData.append('resume', file);

    try {
      const response = await fetch(`${API_BASE_URL}/profile/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload resume');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Upload resume error:', error);
      
      // For demo mode, return mock data
      if (process.env.NODE_ENV === 'development' && token.startsWith('mock-')) {
        return {
          url: '/mock-resume.pdf',
          uploadedAt: new Date().toISOString()
        };
      }
      
      throw error;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

const authService = new AuthService();
export default authService;